// PurchaseOrderViewer.jsx

import React, { useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// CSS
import './PurchaseOrderViewer.css';

// Components
import Button from 'components/button/Button';
import PurchaseOrderGeneralInfo from 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo';
import PurchaseOrderItems from 'nodetypes/purchaseorder/view/PurchaseOrderItems';
import PurchaseOrderMemo from 'nodetypes/purchaseorder/view/PurchaseOrderMemo';
import PurchaseOrderPayments from 'nodetypes/purchaseorder/view/PurchaseOrderPayments';
import PurchaseOrderPreviewModal from 'nodetypes/purchaseorder/view/PurchaseOrderPreviewModal';
import PurchaseOrderSignatures from 'nodetypes/purchaseorder/view/PurchaseOrderSignatures';
import PurchaseOrderTerms from 'nodetypes/purchaseorder/view/PurchaseOrderTerms';
import usePurchaseOrderRelatedNodes from 'nodetypes/purchaseorder/view/usePurchaseOrderRelatedNodes';

/**
 * PurchaseOrderViewer
 *
 * Workspace Highlight-slot screen for PURCHASE_ORDER.
 * General Info + Items + Payments + Terms + Memo + Signature.
 *
 * @param {object} node
 * @param {object|null} [nodeType]
 * @param {boolean} [readOnly=false]
 * @param {function} [onNodeChange]
 * @param {function|null} [onRelatedNodeOpen]
 * @param {object[]|null} [neighborNodes]
 * @param {object|null} [portal]
 * @param {object|null} [letterheadOverride]
 * @param {object|null} [relatedNodesByKey]
 * @returns {ReactNode}
 */
const PurchaseOrderViewer = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  onRelatedNodeOpen = null,
  neighborNodes: neighborNodesProp = null,
  portal = null,
  letterheadOverride = null,
  relatedNodesByKey = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();

  // ----- STATE ----- //

  // Preview modal open state
  const [previewOpen, setPreviewOpen] = useState(false);

  // ----- MEMOS / HOOKS ----- //

  const baseKey = String(activeBase?.key || node?.baseKey || '').trim();
  const subscribedNeighbors = usePurchaseOrderRelatedNodes(
    neighborNodesProp == null ? node?.key : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div className="purchase-order-viewer">
      <div className="purchase-order-viewer-toolbar">
        <Button
          label="Preview"
          iconKey="visibility"
          height="30px"
          onClick={() => setPreviewOpen(true)}
        />
      </div>

      <section className="purchase-order-viewer-section">
        <div className="purchase-order-viewer-section-header">
          <div className="purchase-order-viewer-section-title">
            General Info
          </div>
        </div>
        <PurchaseOrderGeneralInfo
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          letterheadOverride={letterheadOverride}
          relatedNodesByKey={relatedNodesByKey}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <PurchaseOrderItems
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <PurchaseOrderPayments
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <PurchaseOrderTerms
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <PurchaseOrderMemo
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <PurchaseOrderSignatures
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          portal={portal}
        />
      </section>

      <PurchaseOrderPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        node={node}
        nodeType={nodeType}
        neighborNodes={neighborNodes}
        letterheadOverride={letterheadOverride}
        relatedNodesByKey={relatedNodesByKey}
      />
    </div>
  );
});

// Dev Tools
PurchaseOrderViewer.displayName = 'PurchaseOrderViewer';

export default PurchaseOrderViewer;
